"use client"

import React, {useMemo} from "react"
import Image from "next/image"
import Skeleton from "react-loading-skeleton"
import AppIcon from "@/app/components/atomic/AppIcon"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import {IBankRateData} from "@/app/models/loanRateComparator"
import {PRETTO_UTM_CONTENT, PRETTO_UTM_TERM} from "@/app/constants/prettoJourney"
import {useTracking} from "@/app/contexts/tracking-context"
import {useAffiliatedLink} from "@/app/(pages)/taux-pret-immobilier/hooks/useAffiliatedLink"

interface Props {
    banksRanked: IBankRateData[]
    isLoading: boolean
    isVariant: boolean
}

const ROW_GRID_COLS = "grid-cols-[1fr_1fr_1fr_1fr_1.4fr] lg:grid-cols-[2fr_1fr_1fr_1fr_1.4fr]"

export default function LoanRateComparatorTable({banksRanked, isLoading, isVariant}: Props) {
    const linkHref = useAffiliatedLink(isVariant, {content: PRETTO_UTM_CONTENT.BANK_TABLE, term: PRETTO_UTM_TERM.TAUX_PRET})
    const shouldOpenInNewTab = !isVariant

    if (isLoading || !banksRanked || banksRanked.length === 0) {
        return <LoanRateComparatorTableSkeleton />
    }

    return (
        <div className="flex flex-col">
            <TableHeader />

            <div className="flex flex-col gap-3">
                {banksRanked.map((bank: IBankRateData) => (
                    <TableRow
                        key={bank.name}
                        bank={bank}
                        linkHref={linkHref}
                        shouldOpenInNewTab={shouldOpenInNewTab}
                        isVariant={isVariant}
                    />
                ))}
            </div>

            <p className="text-end text-pale/60 text-sm mt-4">
                *Ces taux sont des taux moyens. Vous devez faire une simulation pour connaître votre taux personnalisé.
            </p>
        </div>
    )
}

function TableHeader() {
    return (
        <div className={`grid ${ROW_GRID_COLS} items-center bg-deep-green text-white rounded-2xl px-3 py-3 lg:px-5 font-semibold sticky top-6 lg:top-28 z-20 mb-3 text-xs lg:text-base gap-2`}>
            <span>Banque</span>
            <span className="text-center">Taux d'intérêt</span>
            <span className="text-center">Évolution</span>
            <span className="text-center">TAEG</span>
            <span className="text-center">Obtenir ce taux</span>
        </div>
    )
}

function TableRow({
                      bank,
                      linkHref,
                      shouldOpenInNewTab,
                      isVariant,
                  }: {
    bank: IBankRateData
    linkHref: string
    shouldOpenInNewTab: boolean
    isVariant: boolean
}) {
    const {track} = useTracking()

    const toDisplayNumber = (number: number | null) => {
        if (number === null) return "N/A"
        return number.toFixed(2).replace(".", ",")
    }

    const trackBankClick = () => {
        const bankNameFormatted = bank.name
            .toLowerCase()
            .replace(/ /g, "-")
            .replace(/'/g, "")
            .replace(/é/g, "e")

        const eventName = isVariant
            ? "gtm_taux_pret_immo_ab_test_bank_click"
            : "gtm_partner_link_clic"

        track({
            event: eventName,
            event_source: `${bankNameFormatted}_cta`,
            partner_url: linkHref,
        })
    }

    return (
        <div
            className={`bg-white rounded-2xl shadow-md border border-rm-gray-light/60 px-3 py-3 lg:px-5 lg:py-4
                       grid ${ROW_GRID_COLS} gap-2 items-center`}
            data-test="bank-row"
        >
            <div className="flex gap-2 lg:gap-3 items-center min-w-0">
                <MemoizedBankLogo name={bank.name} />
                <span className="hidden lg:inline text-pale font-medium truncate">{bank.name}</span>
            </div>

            <div
                className="flex justify-center items-center text-pale font-bold text-base lg:text-lg"
                data-test="interest-rate"
            >
                {toDisplayNumber(bank.interestRate)} %*
            </div>

            <div className="flex justify-center items-center">
                <AppIcon iconClassName={bank.evolutionClass} size={"2xl"} type={"duotone"} />
            </div>

            <div className="flex justify-center items-center text-pale text-sm lg:text-base">
                {toDisplayNumber(bank.taeg)} %*
            </div>

            <div className="flex flex-col items-center gap-1 min-w-0">
                <AppButton
                    href={linkHref}
                    target={shouldOpenInNewTab ? "_blank" : "_self"}
                    onClick={trackBankClick}
                    theme="orange"
                    size="xs"
                    arrowPosition="none"
                    wFull
                    dataTest="bank-simulation-btn"
                >
                    <span className="hidden lg:inline">Simulation</span>
                    <span className="inline lg:hidden">Simuler</span>
                </AppButton>
                <small className="hidden lg:inline text-pale/60 whitespace-nowrap">Sans engagement</small>
            </div>
        </div>
    )
}

function BankLogo({name}: { name: string }) {
    const getBankLogoPath = useMemo(
        () => (n: string) => {
            const normalized = n
                .toLowerCase()
                .replace(/ /g, "_")
                .normalize("NFD")
                .replace(/[̀-ͯ]/g, "")
                .replace(/'/g, "")
            return `/images/bankLogos/logo_${normalized}.png`
        },
        [],
    )

    return (
        <div className="relative h-10 w-10 lg:h-14 lg:w-14 shrink-0">
            <Image className="object-contain" src={getBankLogoPath(name)} alt={name} fill />
        </div>
    )
}

const MemoizedBankLogo = React.memo(BankLogo, (prev, next) => prev.name === next.name)

export function LoanRateComparatorTableSkeleton() {
    return (
        <div className="flex flex-col gap-3">
            <div className={`grid ${ROW_GRID_COLS} items-center bg-deep-green text-white rounded-2xl px-3 py-3 lg:px-5 font-semibold mb-1 text-xs lg:text-base gap-2`}>
                <span>Banque</span>
                <span className="text-center">Taux d'intérêt</span>
                <span className="text-center">Évolution</span>
                <span className="text-center">TAEG</span>
                <span className="text-center">Obtenir ce taux</span>
            </div>

            {[...Array(10)].map((_, index) => (
                <div
                    key={index}
                    className={`bg-white rounded-2xl shadow-md border border-rm-gray-light/60 px-3 py-3 lg:px-5 lg:py-4
                               grid ${ROW_GRID_COLS} gap-2 items-center`}
                >
                    <div className="flex items-center gap-2 lg:gap-3">
                        <Skeleton circle inline height={40} width={40} containerClassName="inline-flex lg:hidden" />
                        <Skeleton circle inline height={56} width={56} containerClassName="hidden lg:inline-flex" />
                        <Skeleton inline width={90} height={16} containerClassName="hidden lg:inline-flex" />
                    </div>

                    <div className="flex justify-center">
                        <Skeleton inline width={50} height={20} />
                    </div>

                    <div className="flex justify-center">
                        <Skeleton circle inline width={24} height={24} />
                    </div>

                    <div className="flex justify-center">
                        <Skeleton inline width={50} height={20} />
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <Skeleton inline height={36} containerClassName="flex w-full" className="w-full bg-pastel-orange/30" />
                        <Skeleton inline width={120} height={10} containerClassName="hidden lg:inline-flex" />
                    </div>
                </div>
            ))}
        </div>
    )
}
